<script lang="ts">
  // Searchable community picker for the admin sidebar. Mirrors MemberSelect's
  // trigger + typeahead-popover pattern, but the roster is the already-loaded
  // `community.list`, so it filters client-side (no server round-trip) and writes
  // the shared selection through setCommunity. Replaces the old native <select>.
  import { activeCommunity, community, setCommunity } from '../community.svelte';
  import type { Community } from '../admin';
  import CommunityLogo from './CommunityLogo.svelte';
  import { dismissable } from './dismissable';

  let open = $state(false);
  let q = $state('');
  let activeIdx = $state(0);
  let inputEl = $state<HTMLInputElement | null>(null);

  const selected = $derived(activeCommunity());

  const filtered = $derived.by(() => {
    const term = q.trim().toLowerCase();
    if (!term) return community.list;
    return community.list.filter(
      (c) => c.name.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term),
    );
  });

  function toggle(): void {
    open = !open;
    if (open) {
      q = '';
      const i = community.list.findIndex((c) => c.id === community.selectedId);
      activeIdx = i < 0 ? 0 : i;
      queueMicrotask(() => inputEl?.focus());
    }
  }

  function onInput(e: Event): void {
    q = (e.target as HTMLInputElement).value;
    activeIdx = 0;
  }

  function choose(c: Community): void {
    setCommunity(c.id);
    open = false;
    q = '';
  }

  // Arrow-key navigation over the filtered list while the search input has focus.
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIdx = Math.min(activeIdx + 1, filtered.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIdx = Math.max(activeIdx - 1, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = filtered[activeIdx];
      if (c) choose(c);
    } else if (e.key === 'Escape') {
      open = false;
    }
  }

</script>

<!-- Community icon. `box` is the size utility ('h-5 w-5' | 'h-6 w-6') — kept as literal
     strings at the call sites so Tailwind emits the classes. -->
{#snippet logo(c: Community | null, box: string)}
  <CommunityLogo name={c?.name} logoUrl={c?.logo_url} size={box} />
{/snippet}

<div class="relative" use:dismissable={{ isOpen: () => open, close: () => (open = false) }}>
  <!-- The trigger IS the header's community heading: same logo size and same display
       type the static heading used before this moved out of the sidebar, so the header
       reads as a title that happens to be switchable rather than as a form control
       dropped into a header. The negative margin lets the hover surface breathe without
       pushing the logo off the header's left alignment. -->
  <button
    type="button"
    class="-ml-1.5 flex min-w-0 items-center gap-2.5 rounded-field px-1.5 py-1 text-left transition-colors
      {open ? 'bg-base-200' : 'hover:bg-base-200'}"
    onclick={toggle}
    aria-haspopup="listbox"
    aria-expanded={open}
    aria-label="Community"
  >
    {@render logo(selected, 'h-7 w-7')}
    <span class="redact min-w-0 truncate font-display text-base font-bold tracking-tight"
      >{selected?.name ?? 'Select community'}</span
    >
    <svg class="h-4 w-4 shrink-0 opacity-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
  </button>

  {#if open}
    <div class="absolute left-0 z-30 mt-1 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-base-300 bg-base-100 p-1.5 shadow-xl">
      <input
        bind:this={inputEl}
        type="search"
        placeholder="Search communities…"
        class="input input-sm input-bordered mb-1.5 w-full bg-base-100 text-xs"
        value={q}
        oninput={onInput}
        onkeydown={onKeydown}
        aria-label="Search communities"
      />
      <div class="max-h-64 overflow-y-auto">
        {#if filtered.length === 0}
          <div class="px-2 py-3 text-center text-xs text-base-content/62">No communities match.</div>
        {:else}
          {#each filtered as c, i (c.id)}
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-base-200"
              class:bg-base-200={i === activeIdx}
              onclick={() => choose(c)}
              onmouseenter={() => (activeIdx = i)}
            >
              {@render logo(c, 'h-6 w-6')}
              <span class="min-w-0 flex-1">
                <span class="redact block truncate text-sm font-medium">{c.name}</span>
                <span class="redact block truncate font-mono text-[0.65rem] text-base-content/65"
                  >{c.slug}{c.total_members != null ? ` · ${c.total_members} members` : ''}</span
                >
              </span>
              {#if c.id === community.selectedId}
                <svg class="h-4 w-4 shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              {/if}
            </button>
          {/each}
        {/if}
      </div>
    </div>
  {/if}
</div>
